import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Car, 
  Map, 
  BookmarkCheck, 
  Settings, 
  LogOut, 
  Menu,
  X,
  ChevronRight,
  Users,
  Compass,
  ClipboardCheck,
  Sun,
  Moon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from 'next-themes';
import MapView from '../components/MapView3D';
import SavedRoutes from '../components/SavedRoutes';
import VehicleProfiles from '../components/VehicleProfiles';
import SettingsPage from '../components/SettingsPage';
import CommunityRoutes from '../components/CommunityRoutes';
import ScenicRoutes from '../components/ScenicRoutes';
import PreTripChecklist from '../components/PreTripChecklist';
import QuickStartTour from '../components/QuickStartTour';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasVehicles, setHasVehicles] = useState(false);
  const [showTour, setShowTour] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);
  const { theme, setTheme } = useTheme();

  // Check if user has vehicles for tour logic
  useEffect(() => {
    const checkVehicles = async () => {
      try {
        const res = await axios.get(`${API}/vehicles`);
        setHasVehicles(res.data.length > 0);
      } catch (e) {
        setHasVehicles(false);
      }
    };
    checkVehicles();
  }, [location.pathname]); // Re-check when navigating

  const navItems = [
    { icon: Map, label: 'Route Planner', path: '/dashboard' },
    { icon: BookmarkCheck, label: 'Saved Routes', path: '/dashboard/saved' },
    { icon: Users, label: 'Community', path: '/dashboard/community' },
    { icon: Compass, label: 'Scenic Routes', path: '/dashboard/scenic' },
    { icon: ClipboardCheck, label: 'Checkliste', path: '/dashboard/checklist' },
    { icon: Car, label: 'Vehicles', path: '/dashboard/vehicles' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur border-b border-border px-4 h-14 flex items-center justify-between ${isNavigating ? 'hidden' : ''}`}>
        <div className="flex items-center gap-2">
          <Car className="w-6 h-6 text-primary" />
          <span className="font-display font-semibold text-foreground">Curvio</span>
        </div>
        <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                data-testid="mobile-theme-toggle-btn"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-btn"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
      </div>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && !isNavigating && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          ${isNavigating ? 'hidden' : sidebarOpen ? 'fixed' : 'hidden'} ${isNavigating ? '' : 'lg:static lg:transform-none lg:flex'}
          inset-y-0 left-0 z-40
          w-64 bg-card border-r border-border
          transform transition-transform duration-200 ease-out
          flex flex-col noise-bg
        `}
      >
        <div className="relative z-10 flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <Car className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <span className="font-display text-lg font-semibold text-foreground block">Curvio</span>
                <span className="text-xs text-muted-foreground">Classic Car Navigation</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-md
                    transition-all duration-150
                    ${isActive(item.path) 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                  `}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {isActive(item.path) && (
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  )}
                </button>
              ))}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground hover:bg-muted mb-1"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span>{theme === 'dark' ? 'Tagmodus' : 'Nachtmodus'}</span>
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:pt-0 pt-14 overflow-hidden w-full relative">
        <Routes>
          <Route path="/" element={<MapView onNavigationChange={setIsNavigating} />} />
          <Route path="/saved" element={<SavedRoutes />} />
          <Route path="/community" element={<CommunityRoutes />} />
          <Route path="/scenic" element={<ScenicRoutes onSelectRoute={(route) => {
            // Navigate to map with the scenic route loaded
            navigate('/dashboard');
          }} />} />
          <Route path="/checklist" element={<PreTripChecklist vehicleType="oldtimer" />} />
          <Route path="/vehicles" element={<VehicleProfiles />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>

      {/* Quick Start Tour for new users */}
      {showTour && (
        <QuickStartTour 
          hasVehicles={hasVehicles} 
          onClose={() => setShowTour(false)} 
        />
      )}
    </div>
  );
};

export default Dashboard;
